import { useState } from 'react';
import { useMutation } from '@apollo/client';
import useConfig from 'hooks/useConfig';
import useMessages from 'hooks/useMessages';
import { UPDATE_PRODUCT_LANGUAGES, ADD_LANGUAGE_AND_TRANSLATE } from 'graphql/secure/mutations';
import { StringParam, useQueryParam } from 'use-query-params';
import { IProduct } from '../types/product.types';
import { ELanguage } from 'DesignSystem/types/language.types';

const useAddLanguage = ({ onClose }: { onClose?: () => void }) => {
  const { setErrorMessage, setSuccessMessage } = useMessages();
  const [selectedLang, setSelectedLang] = useState<ELanguage>();

  const config = useConfig();
  const [, setSelectedLangQuery] = useQueryParam(config.appQueryParams.lang, StringParam);

  const [updateProductLanguages, { loading: loadingBlankLanguage }] = useMutation(
    UPDATE_PRODUCT_LANGUAGES,
    {
      onError: () =>
        setErrorMessage('Something went wrong adding the language. Please try again later.'),
      onCompleted: () => {
        setSuccessMessage('Language has been added');
        onClose?.();
        setSelectedLangQuery(selectedLang);
      },
    }
  );

  const [addLangAndTranslate, { loading: loadingLanguageTranslate }] = useMutation(
    ADD_LANGUAGE_AND_TRANSLATE,
    {
      onError: () =>
        setErrorMessage('Something went wrong adding the language. Please try again later.'),
      onCompleted: () => {
        setSuccessMessage('Your content has been successfully translated');
        onClose?.();
        setSelectedLangQuery(selectedLang);
      },
    }
  );

  const addBlankLanguage: (args: { product: IProduct; lang: string }) => void = async ({
    product,
    lang,
  }) => {
    updateProductLanguages({
      variables: {
        id: product.id,
        input: {
          languageConfig: {
            ...product.languageConfig,
            enabled: [...product.languageConfig.enabled, lang],
          },
        },
      },
    });
  };

  const addLanguageAndTranslate: (args: {
    autoTranslateLang: ELanguage | undefined;
    product: IProduct;
    lang: string;
    enabledLanguages: string[];
  }) => void = async ({ autoTranslateLang, product, lang, enabledLanguages }) => {
    await addLangAndTranslate({
      variables: {
        id: product.id,
        translateInput: {
          sourceLang: autoTranslateLang,
          targetLang: lang,
          forceOverwrite: true,
        },
        languageInput: {
          languageConfig: {
            ...product.languageConfig,
            enabled: enabledLanguages,
          },
        },
      },
    });
  };

  return {
    selectedLang,
    loadingBlankLanguage,
    loadingLanguageTranslate,
    setSelectedLang,
    addBlankLanguage,
    addLanguageAndTranslate,
  };
};

export default useAddLanguage;
